// useEnquiryBadge.js
// Tracks unread enquiry/order updates per-doc using reqState-based seen tracking.
// seen[docId] = last reqState the user acknowledged for that doc.
// A dot shows when the doc is in a trigger state the user hasn't seen yet.

import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getFirebase } from '@services/firebase';

const SEEN_KEY = 'enquirySeenDocs'; // { [docId]: seenReqState }

// States that show a Buyer dot
const BUYER_TRIGGER_STATES = new Set([
  'New Request',
  'Review Pending',
  'Review In-Progress',
  'Order On-hold',
  'Order Cancelled',
  'Order In-Progress',
  'Delivery In-Progress',
]);
// 'Order Received' is also a Buyer trigger but ONLY when resuming from on-hold/cancelled.
// Handled as a special case in recount() below.

// States that show a Super dot
const SUPER_TRIGGER_STATES = new Set(['Request Review', 'Order Received']);

// States that auto-clear when the Enquiry page is opened (exported for Enquiry.jsx)
// Buyer: only 'Review Pending' and the resumed-order 'Order Received' clear on page open.
// All other Buyer trigger states clear only when the large card is opened.
export const BUYER_PAGE_RESET_STATES = new Set(['Review Pending', 'Order Received']);
export const SUPER_PAGE_RESET_STATES = new Set(['Order Received']);

const getSeenDocs = () => {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) || '{}'); }
  catch { return {}; }
};

const saveSeenDocs = (obj) => localStorage.setItem(SEEN_KEY, JSON.stringify(obj));

// Mark a doc as seen in its current reqState (call on card open or page-level reset)
export const markDocAsSeen = (docId, reqState) => {
  if (!docId) return;
  const seen = getSeenDocs();
  seen[docId] = reqState || '';
  saveSeenDocs(seen);
  window.dispatchEvent(new Event('enquiry-badge-update'));
};

export const clearEnquiryBadge = () => {
  localStorage.removeItem(SEEN_KEY);
  window.dispatchEvent(new Event('enquiry-badge-update'));
};

export const useEnquiryBadge = (user, userRole) => {
  const [hasUnread, setHasUnread] = useState(false);
  const docsRef = useRef({ enq: [], ord: [] });

  const recount = useCallback(() => {
    if (!user || (userRole !== 'Buyer' && userRole !== 'Super')) {
      setHasUnread(false);
      return;
    }
    const seen = getSeenDocs();
    const found = [...docsRef.current.enq, ...docsRef.current.ord].some(doc => {
      const { reqState } = doc.data();
      const prevSeen = seen[doc.id] || '';
      if (userRole === 'Buyer') {
        if (BUYER_TRIGGER_STATES.has(reqState)) return prevSeen !== reqState;
        // Special: Buyer gets a dot when order is resumed from on-hold or cancelled
        if (reqState === 'Order Received' &&
            (prevSeen === 'Order On-hold' || prevSeen === 'Order Cancelled')) return true;
        return false;
      }
      return SUPER_TRIGGER_STATES.has(reqState) && prevSeen !== reqState;
    });
    setHasUnread(found);
  }, [user, userRole]);

  useEffect(() => {
    window.addEventListener('enquiry-badge-update', recount);
    return () => window.removeEventListener('enquiry-badge-update', recount);
  }, [recount]);

  useEffect(() => {
    const { db } = getFirebase();
    if (!user || !db || (userRole !== 'Buyer' && userRole !== 'Super')) {
      setHasUnread(false);
      return;
    }
    const unsubs = [];
    if (userRole === 'Buyer') {
      unsubs.push(onSnapshot(query(collection(db, 'enquiry'), where('userId', '==', user.uid)),
        snap => { docsRef.current.enq = snap.docs; recount(); }, () => {}));
      unsubs.push(onSnapshot(query(collection(db, 'order'), where('userId', '==', user.uid)),
        snap => { docsRef.current.ord = snap.docs; recount(); }, () => {}));
    } else if (userRole === 'Super') {
      unsubs.push(onSnapshot(query(collection(db, 'enquiry')),
        snap => { docsRef.current.enq = snap.docs; recount(); }, () => {}));
      unsubs.push(onSnapshot(query(collection(db, 'order')),
        snap => { docsRef.current.ord = snap.docs; recount(); }, () => {}));
    }
    return () => unsubs.forEach(u => u());
  }, [user, userRole, recount]);

  return hasUnread;
};
